import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

const ProviderExtensionIdentifier = TrimmedNonEmptyString.pipe(
  Schema.check(Schema.isMaxLength(256)),
);
const ProviderExtensionSource = TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(512)));
const ProviderExtensionCatalogId = TrimmedNonEmptyString.pipe(
  Schema.check(Schema.isMaxLength(768)),
);
const ProviderExtensionUrl = TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(2_048)));
const ProviderExtensionName = TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(512)));
const ProviderExtensionSummary = TrimmedNonEmptyString.pipe(
  Schema.check(Schema.isMaxLength(8_192)),
);
const ProviderExtensionPath = TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(2_048)));

export const ProviderConnectorAuthStatus = Schema.Literals([
  "connected",
  "not_required",
  "needs_authentication",
  "pending_approval",
  "unavailable",
  "unknown",
]);
export type ProviderConnectorAuthStatus = typeof ProviderConnectorAuthStatus.Type;

export const SkillsShSourceType = Schema.Literals(["github", "well-known", "unknown"]);
export type SkillsShSourceType = typeof SkillsShSourceType.Type;

export const SkillsShCatalogSource = Schema.Literals(["v1", "legacy"]);
export type SkillsShCatalogSource = typeof SkillsShCatalogSource.Type;

export const SkillsShSkill = Schema.Struct({
  id: ProviderExtensionCatalogId,
  skillId: ProviderExtensionIdentifier,
  name: ProviderExtensionName,
  source: ProviderExtensionSource,
  installs: NonNegativeInt,
  url: ProviderExtensionUrl,
  sourceType: SkillsShSourceType,
  installUrl: Schema.optional(ProviderExtensionUrl),
  isDuplicate: Schema.optional(Schema.Boolean),
});
export type SkillsShSkill = typeof SkillsShSkill.Type;

export const ProviderSkillsSearchInput = Schema.Struct({
  query: Schema.optional(
    TrimmedNonEmptyString.pipe(
      Schema.check(Schema.isMinLength(2)),
      Schema.check(Schema.isMaxLength(120)),
    ),
  ),
  view: Schema.optional(Schema.Literals(["trending", "hot", "curated"])),
  page: Schema.optional(NonNegativeInt.pipe(Schema.check(Schema.isLessThanOrEqualTo(500)))),
});
export type ProviderSkillsSearchInput = typeof ProviderSkillsSearchInput.Type;

export const ProviderSkillsSearchResult = Schema.Struct({
  query: Schema.optional(TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(120)))),
  skills: Schema.Array(SkillsShSkill).check(Schema.isMaxLength(500)),
  source: SkillsShCatalogSource,
  page: NonNegativeInt,
  hasMore: Schema.Boolean,
});
export type ProviderSkillsSearchResult = typeof ProviderSkillsSearchResult.Type;

export const ProviderSkillAgent = Schema.Literals(["codex", "claude-code"]);
export type ProviderSkillAgent = typeof ProviderSkillAgent.Type;

export const ProviderSkillInstallInput = Schema.Struct({
  id: Schema.optional(ProviderExtensionCatalogId),
  source: ProviderExtensionSource,
  skillId: ProviderExtensionIdentifier,
  installUrl: Schema.optional(ProviderExtensionUrl),
  sourceType: Schema.optional(SkillsShSourceType),
  expectedContentHash: Schema.optional(ProviderExtensionIdentifier),
  allowUnaudited: Schema.optional(Schema.Boolean),
});
export type ProviderSkillInstallInput = typeof ProviderSkillInstallInput.Type;

export const ProviderSkillInstallTargetStatus = Schema.Literals([
  "installed",
  "already_installed",
  "failed",
  "verification_failed",
]);
export type ProviderSkillInstallTargetStatus = typeof ProviderSkillInstallTargetStatus.Type;

export const ProviderSkillInstallTarget = Schema.Struct({
  instanceId: ProviderInstanceId,
  providerName: TrimmedNonEmptyString,
  agent: ProviderSkillAgent,
  status: ProviderSkillInstallTargetStatus,
  message: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderSkillInstallTarget = typeof ProviderSkillInstallTarget.Type;

export const ProviderSkillInstallResult = Schema.Struct({
  source: ProviderExtensionSource,
  skillId: ProviderExtensionIdentifier,
  agents: Schema.Array(ProviderSkillAgent).check(Schema.isMaxLength(2)),
  status: Schema.Literals(["installed", "partial", "failed"]),
  targets: Schema.Array(ProviderSkillInstallTarget).check(Schema.isMaxLength(32)),
  sourceRef: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderSkillInstallResult = typeof ProviderSkillInstallResult.Type;

export const SkillsShAuditVerdict = Schema.Literals(["pass", "warn", "fail", "unavailable"]);
export type SkillsShAuditVerdict = typeof SkillsShAuditVerdict.Type;

export const SkillsShAudit = Schema.Struct({
  provider: ProviderExtensionIdentifier,
  status: Schema.Literals(["pass", "warn", "fail"]),
  summary: ProviderExtensionSummary,
  riskLevel: Schema.optional(ProviderExtensionIdentifier),
  auditedAt: Schema.optional(TrimmedNonEmptyString.pipe(Schema.check(Schema.isMaxLength(128)))),
});
export type SkillsShAudit = typeof SkillsShAudit.Type;

export const ProviderSkillReviewInput = Schema.Struct({
  id: ProviderExtensionCatalogId,
  source: ProviderExtensionSource,
  skillId: ProviderExtensionIdentifier,
});
export type ProviderSkillReviewInput = typeof ProviderSkillReviewInput.Type;

export const ProviderSkillReviewResult = Schema.Struct({
  id: ProviderExtensionCatalogId,
  source: ProviderExtensionSource,
  skillId: ProviderExtensionIdentifier,
  verdict: SkillsShAuditVerdict,
  audits: Schema.Array(SkillsShAudit).check(Schema.isMaxLength(32)),
  contentHash: Schema.optional(ProviderExtensionIdentifier),
  filePaths: Schema.Array(ProviderExtensionPath).check(Schema.isMaxLength(512)),
});
export type ProviderSkillReviewResult = typeof ProviderSkillReviewResult.Type;

export const ProviderSkillMutationInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  skillId: ProviderExtensionIdentifier,
});
export type ProviderSkillMutationInput = typeof ProviderSkillMutationInput.Type;

export const ProviderSkillMutationResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  skillId: TrimmedNonEmptyString,
  status: Schema.Literals(["updated", "up_to_date", "removed"]),
});
export type ProviderSkillMutationResult = typeof ProviderSkillMutationResult.Type;

export const ProviderConnectorConnectInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  name: ProviderExtensionIdentifier,
});
export type ProviderConnectorConnectInput = typeof ProviderConnectorConnectInput.Type;

export const ProviderConnectorConnectResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  name: TrimmedNonEmptyString,
  status: Schema.Literals(["connected", "ready"]),
  authStatus: ProviderConnectorAuthStatus,
  toolCount: Schema.optional(NonNegativeInt),
});
export type ProviderConnectorConnectResult = typeof ProviderConnectorConnectResult.Type;

export const ProviderConnectorAddInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  name: ProviderExtensionIdentifier,
  url: ProviderExtensionUrl,
});
export type ProviderConnectorAddInput = typeof ProviderConnectorAddInput.Type;

export const ProviderConnectorMutationInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  name: ProviderExtensionIdentifier,
});
export type ProviderConnectorMutationInput = typeof ProviderConnectorMutationInput.Type;

export const ProviderConnectorMutationResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  name: TrimmedNonEmptyString,
  status: Schema.Literals(["added", "disconnected", "removed"]),
});
export type ProviderConnectorMutationResult = typeof ProviderConnectorMutationResult.Type;

export const ProviderExtensionOperation = Schema.Literals([
  "search_skills",
  "review_skill",
  "install_skill",
  "update_skill",
  "remove_skill",
  "add_connector",
  "connect_connector",
  "disconnect_connector",
  "remove_connector",
]);
export type ProviderExtensionOperation = typeof ProviderExtensionOperation.Type;

export const ProviderExtensionErrorReason = Schema.Literals([
  "catalog_unavailable",
  "catalog_authentication_required",
  "invalid_skill",
  "skill_not_found",
  "audit_failed",
  "audit_required",
  "installer_unavailable",
  "install_failed",
  "install_timed_out",
  "verification_failed",
  "no_supported_provider",
  "provider_not_found",
  "operation_not_supported",
  "invalid_connector",
  "connector_already_exists",
  "connector_not_found",
  "authentication_not_supported",
  "authentication_incomplete",
  "authentication_failed",
  "authentication_timed_out",
  "connector_operation_failed",
]);
export type ProviderExtensionErrorReason = typeof ProviderExtensionErrorReason.Type;

export class ProviderExtensionError extends Schema.TaggedErrorClass<ProviderExtensionError>()(
  "ProviderExtensionError",
  {
    operation: ProviderExtensionOperation,
    reason: ProviderExtensionErrorReason,
    message: TrimmedNonEmptyString,
  },
) {}
